import React, { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
import { View } from 'react-native';
import Animated, { useAnimatedScrollHandler, useSharedValue } from 'react-native-reanimated';
import { AppView, Button, CardView, NavigationHeader } from 'design-system';
import BottomPanel from 'design-system/BottomPanel';
import { HEIGHT_PLUS_PADDING } from 'design-system/Button';
import { DaysOfTheMonthSelector } from 'design-system/date';
import ScreenInfoHeader from 'design-system/ScreenInfoHeader';
import StatusBar from 'design-system/StatusBar';
import Text from 'design-system/Text';
import { rem, SCROLL_INDICATOR_INSETS } from 'design-system/values';
import { muteErrorAndResult, useFetchOld } from 'hooks';
import useAppFrameDimensions from 'hooks/useAppFrameDimensions';
import { useMarginBottom } from 'hooks/useMarginBottom';
import useStyles from 'hooks/useStyles';
import HelpScreenButton from 'features/help/components/HelpScreenButton';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { getOrdinal } from 'utils/formatting';
import createStyleSheets from 'utils/createStyleSheets';
import useColors from 'hooks/useColors';
import Amplitude from 'utils/amplitude';
import { editAutoInvestPlan } from '../actions/autoinvest';
import useNavigateToNextScreen from '../hooks/useNavigateToNextScreen';
import { selectActiveTradingAccountId } from '../reducers/selectors';
import { clearOnboardingState } from '../actions';
const AutoinvestPickCollectionDateScreen = ({ route, navigation, }) => {
    const dispatch = useAppDispatch();
    const colors = useColors();
    const styles = useStyles(styleSet);
    const { initialCollectionDate, planId } = route.params;
    const isEditMode = initialCollectionDate !== undefined;
    const [selected, setSelected] = useState(initialCollectionDate ?? 1);
    const [, , loadingAction, fetch] = useFetchOld(muteErrorAndResult);
    const tradingAccountId = useAppSelector(selectActiveTradingAccountId);
    const onboarding = useAppSelector((state) => state.invest.onboarding);
    const scrollY = useSharedValue(0);
    const mountedOnAnotherScreen = route.params?.mountedOnAnotherScreen;
    const initialAllocations = route.params?.initialAllocations;
    useLayoutEffect(() => {
        navigation.setOptions({
            headerTintColor: colors.text.primary,
        });
    }, [colors.text.primary]);
    useEffect(() => {
        if (onboarding && !mountedOnAnotherScreen) {
            Amplitude.logEvent('Select Collection Date', { source: 'invest' });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const handleChange = useCallback((days) => {
        setSelected(days[0]);
    }, []);
    const { paddingHorizontalStyle } = useAppFrameDimensions(mountedOnAnotherScreen);
    const paddingBottom = useMarginBottom('paddingBottom', HEIGHT_PLUS_PADDING, paddingHorizontalStyle);
    const navigateToNextScreen = useNavigateToNextScreen();
    const handleContinue = useCallback(async () => {
        if (!tradingAccountId) {
            if (onboarding) {
                // case for onboarding
                dispatch({ type: 'SAVE_ONBOARDING_STATE', autoinvestDayOfMonth: selected });
                navigateToNextScreen();
            }
            else {
                dispatch(clearOnboardingState());
                navigation.popToTop();
            }
            return;
        }
        if (isEditMode && planId) {
            const result = await fetch(editAutoInvestPlan(planId, { dayOfMonth: selected }));
            if (result) {
                navigation.goBack();
            }
            return;
        }
        navigation.navigate('AutoinvestChooseAmount', {
            accountType: route.params.accountType,
            collectionDate: selected,
            initialAllocations,
        });
    }, [
        tradingAccountId,
        isEditMode,
        planId,
        route.params.accountType,
        selected,
        initialAllocations,
        onboarding,
        navigateToNextScreen,
    ]);
    const headerColors = useMemo(() => [colors.background.dark, colors.cards.onDark], [colors.background.dark, colors.cards.onDark]);
    const scrollHandler = useAnimatedScrollHandler(({ contentOffset }) => {
        scrollY.value = contentOffset.y;
    });
    return (<AppView withFrame={false}>
      <StatusBar barStyle="dark-content" animated/>
      <NavigationHeader scrollY={scrollY} hideTitleOnScroll regularTitleHeight outputColors={headerColors} isInTab={!!mountedOnAnotherScreen} title="" rightContent={<View style={mountedOnAnotherScreen ? styles.headerRight : null}>
            <HelpScreenButton />
          </View>}/>
      <Animated.ScrollView contentContainerStyle={paddingBottom} onScroll={scrollHandler} scrollEventThrottle={16} scrollIndicatorInsets={SCROLL_INDICATOR_INSETS}>
        <ScreenInfoHeader title={isEditMode ? 'Edit your collection date' : 'Select your collection date'} subTitle="We’re setting up Autoinvest for your monthly investment. Please pick the date to collect your money"/>
        <CardView style={styles.calendarContainer} noMarginTop>
          <DaysOfTheMonthSelector dayStyles={styles.dayStyles} days={[selected]} maxNumberOfDays={1} totalDays={28} onChange={handleChange}/>
        </CardView>
      </Animated.ScrollView>
      <BottomPanel isInTab={!!mountedOnAnotherScreen}>
        <View style={styles.bottomTextContainer}>
          <Text TextThin-16 Secondary centered>
            Your Autoinvest will run on the{' '}
            <Text Text-16 Secondary>
              {getOrdinal(selected)} day of each month
            </Text>
          </Text>
        </View>
        <Button brand loading={loadingAction} onPress={handleContinue} title={isEditMode ? 'Save' : 'Continue'} disabled={isEditMode ? selected === initialCollectionDate : false}/>
      </BottomPanel>
    </AppView>);
};
const styleSet = createStyleSheets((colors) => ({
    calendarContainer: {
        paddingHorizontal: rem(12),
        paddingTop: rem(16),
    },
    bottomTextContainer: {
        paddingHorizontal: rem(24),
        paddingBottom: rem(8),
    },
    dayStyles: {
        backgroundColor: colors.background.dark,
    },
    headerRight: {
        alignItems: 'flex-end',
        paddingRight: rem(16),
        marginBottom: rem(6),
    },
}));
export default AutoinvestPickCollectionDateScreen;
